import { useState, isValidElement, type ReactNode } from 'react'
import { Typography } from './Typography'

export interface StatCardProps {
  variant?: 'default' | 'filled' | 'teal'
  size?: 'sm' | 'md'
  title?: ReactNode
  label?: string
  singleLineTitle?: boolean
  value?: string | number | any
  suffix?: string
  note?: string
  subtext?: string
  caption?: string
  urgent?: string
  newLabel?: string
  trendLabel?: string
  trendDirection?: 'up' | 'down'
  icon?: ReactNode
  iconTone?: string
  iconWrapClassName?: string
  iconPosition?: 'left' | 'right'
  labelPosition?: 'top' | 'bottom'
  onClick?: () => void
  isSelected?: boolean
  selected?: boolean
  className?: string
  children?: ReactNode
  accent?: 'green' | 'red' | 'amber' | 'blue'
}

function sanitizeClassName(className?: string): string {
  if (!className) return ''
  return className
}

export function StatCard({
  variant = 'default',
  size = 'md',
  title,
  label,
  value,
  suffix,
  note,
  subtext,
  caption,
  urgent,
  newLabel,
  trendLabel,
  trendDirection,
  icon,
  iconTone,
  iconWrapClassName,
  iconPosition,
  labelPosition,
  onClick,
  isSelected,
  selected,
  className = '',
  children,
  accent,
}: StatCardProps) {
  const [internalSelected, setInternalSelected] = useState(false)
  const isCardSelected = isSelected ?? selected ?? internalSelected
  const isTeal = variant === 'filled' || variant === 'teal'

  const displayTitle = title || label
  const displaySubtext = note || subtext || caption

  const safeValue =
    typeof value === 'object' && value !== null && !isValidElement(value)
      ? (value as any).count ?? (value as any).value ?? (value as any).status ?? (value as any).total ?? ''
      : value

  const safeSubtext =
    typeof displaySubtext === 'object' && displaySubtext !== null && !isValidElement(displaySubtext)
      ? (displaySubtext as any).subtitle ?? (displaySubtext as any).subtext ?? ''
      : displaySubtext

  const handleClick = () => {
    if (isSelected === undefined && selected === undefined) {
      setInternalSelected((prev) => !prev)
    }
    onClick?.()
  }

  const isInteractive = Boolean(onClick || isSelected !== undefined || selected !== undefined)

  const stateClasses = accent
    ? accent === 'green'
      ? 'bg-success-50 border-success-100 text-success-500'
      : accent === 'red'
        ? 'bg-error-50 border-error-100 text-error-500'
        : accent === 'amber'
          ? 'bg-warning-50 border-warning-100 text-warning-text'
          : 'bg-blue-50 border-blue-300 text-blue-700'
    : isTeal
      ? isCardSelected
        ? 'bg-[#0f565d] border-[#0f565d] text-white shadow-md'
        : 'bg-[#146971] border-[#146971] text-white hover:bg-[#0f565d] hover:border-[#0f565d] shadow-sm'
      : labelPosition === 'top' || iconPosition === 'right'
        ? 'bg-white border-[#005156]/20 shadow-xs'
        : isCardSelected
          ? 'bg-[#f2faf9] !border-[#0F565D] shadow-none'
          : 'bg-white border-[#5daea7] hover:!border-[#0F565D] hover:shadow-[0_0_14px_1.2px_rgba(0,0,0,0.25)]'

  const safeClassName = sanitizeClassName(className)
  const variantClass = isTeal ? 'kpi-card-filled' : 'kpi-card-default'

  const Component = isInteractive ? 'button' : 'div'

  return (
    <Component
      {...(isInteractive ? { type: 'button', onClick: handleClick } : {})}
      aria-selected={isCardSelected}
      data-selected={isCardSelected}
      data-no-cta-proximity="true"
      className={`${isInteractive ? 'kpi-card-interaction cursor-pointer' : 'cursor-default'} ${variantClass} relative w-full rounded-[12px] border text-left transition-all duration-200 ease-in-out box-border hover:-translate-y-0.5 hover:shadow-md ${
        size === 'sm' ? 'px-3 py-2 min-h-[72px]' : 'px-4 py-3 min-h-[86px]'
      } ${stateClasses} ${safeClassName}`}
    >
      {children ? (
        children
      ) : (
        <>
          {isTeal ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                {displayTitle ? (
                  <div className="min-w-0">
                    <Typography
                      as="p"
                      className="text-btn-tiny uppercase text-[#a5f3fc] leading-snug break-words"
                      weight="bold"
                    >
                      {displayTitle}
                    </Typography>
                  </div>
                ) : null}
                {icon ? (
                  <span
                    className={`inline-flex items-center justify-center rounded-md shrink-0 ${
                      size === 'sm' ? 'h-7 w-7' : 'h-8 w-8'
                    } ${
                      iconTone || iconWrapClassName || 'bg-white/20 text-white'
                    }`}
                  >
                    {icon}
                  </span>
                ) : null}
              </div>

              {safeValue !== undefined && safeValue !== '' ? (
                <div className="text-[18px] sm:text-[20px] font-extrabold tracking-tight text-white">
                  {safeValue}
                </div>
              ) : null}

              {safeSubtext ? (
                <Typography
                  as="p"
                  variant="b4"
                  className="text-btn-tiny text-white/90 leading-tight break-words"
                >
                  {safeSubtext}
                </Typography>
              ) : null}
            </div>
          ) : labelPosition === 'top' || iconPosition === 'right' ? (
            <div className="flex flex-col justify-between w-full min-w-0 h-full">
              <div className="flex items-start justify-between gap-2 w-full min-w-0">
                {displayTitle ? (
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] truncate pt-0.5 min-w-0 flex-1">
                    {displayTitle}
                  </div>
                ) : <div className="flex-1" />}

                {icon ? (
                  <span
                    className={`inline-flex items-center justify-center rounded-lg shrink-0 ${
                      size === 'sm' ? 'h-7 w-7' : 'h-8 w-8'
                    } ${
                      iconTone || iconWrapClassName || 'bg-[#E6F4F1] text-[#005156]'
                    }`}
                  >
                    {icon}
                  </span>
                ) : null}
              </div>

              <div className="mt-2 min-w-0">
                {safeValue !== undefined && safeValue !== '' ? (
                  <div className="text-[18px] sm:text-[20px] font-extrabold text-[#0F172A] truncate leading-tight">
                    {safeValue}
                  </div>
                ) : null}

                {safeSubtext ? (
                  <div className="text-xs text-[#64748B] truncate mt-1">
                    {safeSubtext}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {(icon || newLabel || urgent) ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {icon ? (
                      <span
                        className={`inline-flex items-center justify-center rounded-md shrink-0 ${
                          size === 'sm' ? 'h-7 w-7' : 'h-8 w-8'
                        } ${
                          iconTone || iconWrapClassName || 'bg-[#fff1f1] text-[#ef5350]'
                        }`}
                      >
                        {icon}
                      </span>
                    ) : null}
                  </div>
                  {newLabel ? (
                    <Typography
                      as="span"
                      className="text-btn-tiny text-[#08786f] shrink-0"
                      weight="bold"
                    >
                      {newLabel}
                    </Typography>
                  ) : null}
                  {urgent ? (
                    <Typography
                      as="span"
                      className="text-btn-tiny text-[#dc2626] shrink-0"
                      weight="bold"
                    >
                      {urgent}
                    </Typography>
                  ) : null}
                </div>
              ) : null}

              <div className={`${size === 'sm' ? 'mt-2' : 'mt-3'} flex items-end gap-1 flex-wrap`}>
                <Typography
                  as="span"
                  className={`text-[18px] sm:text-[20px] break-all leading-tight font-extrabold ${
                    accent === 'green'
                      ? 'text-success-500'
                      : accent === 'red'
                        ? 'text-error-500'
                        : accent === 'amber'
                          ? 'text-warning-text'
                          : accent === 'blue'
                            ? 'text-blue-700'
                            : 'text-[#1f2937]'
                  }`}
                  weight="bold"
                >
                  {safeValue !== undefined && safeValue !== '' ? safeValue : '\u00A0'}
                </Typography>
                {suffix ? (
                  <Typography
                    as="span"
                    className="pb-1 text-btn-tiny text-[#7b8796]"
                  >
                    {suffix}
                  </Typography>
                ) : null}
              </div>

              {displayTitle ? (
                <div className={`${size === 'sm' ? 'mt-0.5' : 'mt-1'} min-w-0`}>
                  <Typography
                    as="p"
                    className={`text-btn-tiny uppercase truncate ${
                      accent === 'green'
                        ? 'text-success-500'
                        : accent === 'red'
                          ? 'text-error-500'
                          : accent === 'amber'
                            ? 'text-warning-text'
                            : accent === 'blue'
                              ? 'text-blue-700'
                              : 'text-[#65748b]'
                    }`}
                    weight="bold"
                  >
                    {displayTitle}
                  </Typography>
                </div>
              ) : null}

              <div className={`${size === 'sm' ? 'mt-0.5 h-4' : 'mt-1.5 h-5'} overflow-hidden`}>
                {safeSubtext ? (
                  <Typography
                    as="p"
                    variant="b4"
                    className="text-btn-tiny text-[#5d6a7a] truncate"
                  >
                    {safeSubtext}
                  </Typography>
                ) : null}
              </div>

              {trendLabel ? (
                <div className="mt-1 inline-flex items-center gap-1">
                  <Typography
                    as="span"
                    variant="b4"
                    weight="semibold"
                    color={trendDirection === 'up' ? 'text-[#4f8c4a]' : 'text-[#ef4444]'}
                  >
                    {trendDirection === 'up' ? '↑' : '↓'} {trendLabel}
                  </Typography>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </Component>
  )
}

export const StatsCard = StatCard
export type StatsCardProps = StatCardProps
